import React, { type ReactNode, useRef } from 'react';

import { type Transition } from 'framer-motion';
import { useTheme } from 'styled-components';

import { Portal, PortalArea } from '@pkg/components';
import { type OutsideClickExclusion, useKeyPress, useOutsideClick } from '@pkg/hooks';
import { noop } from '@pkg/utils';

import { BackdropVariant, backdropVariants, DrawerVariant, drawerVariants } from './Drawer.motion';
import { Footer, Header, Main } from './sections';

import { Backdrop, Interior, StyledDrawer } from './styles';

export interface DrawerProps {
    children: ReactNode;
    // TODO: Not useful at the moment... but eventually this will
    // get rendered as a result of a `resizeable` variant.
    backdrop?: boolean;
    closeExclusions?: OutsideClickExclusion;
    onClose?(): void;
}

// TODO: Missing the following features:
// 1. Focus trapping
// 2. Body scroll locking
// 3. Accessibility attributes
export function Drawer({ children, backdrop = false, closeExclusions, onClose }: DrawerProps) {
    const drawerRef = useRef(null);
    const theme = useTheme();

    // Worth considering removing this, since on `mobile`
    // we don't really have `Escape` key presses.
    useKeyPress(['Escape'], onClose ?? noop, {
        disabled: !onClose,
    });

    useOutsideClick(drawerRef.current, onClose ?? noop, {
        exclude: closeExclusions,
        disabled: !onClose,
    });

    // We might want to `delay` ONLY the "entrance" animation
    // on the <StyledDrawer /> when `backdrop` is `true`.
    const commonTransition: Transition = {
        ease: theme.transitions.eases.cubic,
        duration: theme.transitions.speeds.fast,
    };

    const backdropMarkup = backdrop ? (
        <Backdrop
            variants={backdropVariants}
            initial={BackdropVariant.Hidden}
            animate={BackdropVariant.Show}
            exit={BackdropVariant.Hidden}
            transition={commonTransition}
        />
    ) : null;

    return (
        <Portal destination={PortalArea.Transition}>
            {backdropMarkup}

            <StyledDrawer
                ref={drawerRef}
                variants={drawerVariants}
                initial={DrawerVariant.Hidden}
                animate={DrawerVariant.Show}
                exit={DrawerVariant.Hidden}
                transition={commonTransition}
            >
                <Interior>{children}</Interior>
            </StyledDrawer>
        </Portal>
    );
}

Drawer.Header = Header;
Drawer.Footer = Footer;
Drawer.Main = Main;
